import React, { useEffect, useRef, useState } from 'react'
import { useI18n } from '../i18n/'
import classNames from 'classnames'
import PlusIcon from '../icon/generated/plus'
import MinusIcon from '../icon/generated/minus'

type GA4AddingOrRemovingElement = 'buyButton' | 'plusSign' | 'input'

export enum SIZE {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

type Props = {
  productName?: string
  quantity: number
  setQuantity: (i: number, addingElementOrigin?: GA4AddingOrRemovingElement) => void
  maxQuantity: number | null
  minQuantity: number
  debounce?: boolean
  background?: 'light-gray' | 'white'
  isMandatoryAccessory?: boolean
  size?: SIZE
  expand?: boolean
  displayValidationMessage?: boolean
}

export const QuantitySelector = ({
  productName,
  quantity,
  setQuantity,
  maxQuantity,
  minQuantity,
  background = 'light-gray',
  isMandatoryAccessory,
  size = SIZE.MEDIUM,
  expand = true,
  displayValidationMessage = true,
}: Props) => {
  const { translate } = useI18n()

  minQuantity = minQuantity ? minQuantity : 0
  maxQuantity = maxQuantity ? maxQuantity : null

  const inputRef = useRef<HTMLInputElement>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [emptyInput, setEmptyInput] = useState(false)
  const [localQuantity, setLocalQuantity] = useState(quantity)

  useEffect(() => {
    const validationError = validateQuantity()
    setValidationError(validationError)
    setEmptyInput(false)
  }, [quantity, maxQuantity, minQuantity])

  const validateQuantity = () => {
    if (maxQuantity && maxQuantity < quantity) {
      return translate('MaxQuantity', { quantity: maxQuantity })
    }

    if (minQuantity > quantity) {
      return translate('MinQuantity', { quantity: minQuantity })
    }

    return null
  }

  const handleOnBlur = (e: string) => {
    const parsedQuantity = parseInt(e)
    setEmptyInput(false)

    if (parsedQuantity !== localQuantity && !isNaN(parsedQuantity)) {
      setLocalQuantity(parsedQuantity)
      setQuantity(parsedQuantity, 'buyButton')
    } else if (isNaN(parsedQuantity)) {
      // when user enters empty value, set inputvalue back to last "saved" value
      setQuantity(localQuantity)
    }
  }

  const handleInputKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (inputRef.current) inputRef.current.blur()
      e.preventDefault()
    }
  }

  const handleQuantityChange = (
    quantityInput: string,
    addingElementOrigin: GA4AddingOrRemovingElement
  ) => {
    const parsedQuantity = parseInt(quantityInput)

    if (quantityInput.length === 0) {
      setEmptyInput(true)
    } else if (emptyInput) {
      setEmptyInput(false)
    }

    if (parsedQuantity > 0) {
      // Preventing removal of item when 0 quantity or string("") in input field
      setQuantity(parsedQuantity, addingElementOrigin)
    }
  }

  return (
    <div
      className={classNames('c-quantity-selector', {
        'c-quantity-selector--white': background === 'white',
        'c-quantity-selector--small': size === SIZE.SMALL,
        'c-quantity-selector--large': size === SIZE.LARGE,
        'c-quantity-selector--no-expand': expand === false,
      })}>
      <div className="c-quantity-selector__selectors">
        {!isMandatoryAccessory && (
          <button
            data-test-id="quantity-selector-decrease"
            type="button"
            className="c-quantity-selector__selector c-quantity-selector__selector--btn"
            aria-label={translate('RemoveQuantity')}
            disabled={quantity <= minQuantity || false}
            onClick={() => (setQuantity(quantity - 1), setLocalQuantity(quantity - 1))}>
            <MinusIcon name="minus" className="i-plus i-16" />
          </button>
        )}
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          aria-label={
            productName
              ? translate('NumberOfProduct', { productName })
              : translate('NumberOfProducts')
          }
          value={emptyInput ? '' : quantity}
          onBlur={e => handleOnBlur(e.target.value)}
          onKeyUp={e => handleInputKeyUp(e)}
          onChange={e => handleQuantityChange(e.target.value, 'input')}
          disabled={isMandatoryAccessory}
          className="c-quantity-selector__selector c-quantity-selector__selector--quantity"
        />
        {!isMandatoryAccessory && (
          <button
            data-test-id="quantity-selector-increase"
            type="button"
            className="c-quantity-selector__selector c-quantity-selector__selector--btn"
            aria-label={translate('AddQuantity')}
            disabled={maxQuantity ? quantity >= maxQuantity : false}
            onClick={() => (setQuantity(quantity + 1, 'plusSign'), setLocalQuantity(quantity + 1))}>
            <PlusIcon name="plus" className="i-plus i-16" />
          </button>
        )}
      </div>

      {validationError && displayValidationMessage && (
        <div className="u-color-danger u-w-100">
          <small>{validationError}</small>
        </div>
      )}
    </div>
  )
}

export default QuantitySelector
